import { CopyOutlined, RollbackOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { Inventory } from '@services';
import { Button, Space, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

interface IProps {
  onDuplicate: (item: Inventory) => void;
  onReturnToSupplier: (item: Inventory) => void;
  onAddToPurchaseOrder: (item: Inventory) => void;
}

export const Columns = ({ onDuplicate, onReturnToSupplier, onAddToPurchaseOrder }: IProps) => [
  {
    title: L('Item'),
    key: 'item',
    render: (_: unknown, record: Inventory) => {
      const meta: Array<{ key: string; label: string; value: React.ReactNode }> = [];

      if (record.creationTime)
        meta.push({ key: 'added', label: 'Added', value: dayjs(record.creationTime).format('DD MMM YYYY') });
      if (record.purchaseDate)
        meta.push({ key: 'purchased', label: 'Purchased', value: dayjs(record.purchaseDate).format('DD MMM YYYY') });
      if (record.partNumber)
        meta.push({ key: 'part', label: 'Part #', value: record.partNumber });
      if (record.nominalCode)
        meta.push({ key: 'nm', label: 'NM Code', value: record.nominalCode });
      if (record.reference)
        meta.push({ key: 'ref', label: 'Ref', value: record.reference });
      if (record.categoryName)
        meta.push({ key: 'cat', label: 'Category', value: record.categoryName });
      if (record.supplierInvoiceNumber)
        meta.push({ key: 'inv', label: 'Inv #', value: record.supplierInvoiceNumber });
      if (record.costPrice != null)
        meta.push({ key: 'cost', label: 'Cost', value: `£${record.costPrice.toFixed(2)}` });
      if (record.margin != null) {
        const marginColor = record.margin > 0 ? '#52c41a' : record.margin < 0 ? '#ff4d4f' : '#8c8c8c';
        meta.push({
          key: 'margin',
          label: 'Margin',
          value: <span style={{ color: marginColor }}>{record.margin.toFixed(1)}%</span>,
        });
      }

      const tooltipTitle = (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{record.name || '-'}</div>
          {meta.map(({ key, label, value }) => (
            <div key={key}>
              <span style={{ opacity: 0.65 }}>{label}: </span>
              {value}
            </div>
          ))}
        </div>
      );

      return (
        <Tooltip placement="topLeft" title={tooltipTitle} mouseEnterDelay={0.5}>
          <div>
            <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {record.name || '-'}
            </div>
            {meta.length > 0 && (
              <div
                style={{
                  fontSize: 11,
                  color: '#8c8c8c',
                  marginTop: 2,
                  lineHeight: 1.6,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0 10px',
                }}
              >
                {meta.map(({ key, label, value }) => (
                  <span key={key}>
                    <span style={{ color: '#bfbfbf' }}>{label}:&nbsp;</span>
                    {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        </Tooltip>
      );
    },
  },
  {
    title: L('Supplier'),
    dataIndex: 'supplierName',
    key: 'supplierName',
    width: 150,
    ellipsis: true,
  },
  {
    title: L('Qty'),
    dataIndex: 'quantity',
    key: 'quantity',
    width: 60,
    render: (val: number, record: Inventory) =>
      record.isLowStock ? (
        <Tooltip title={L('Stock at or below minimum')}>
          <Tag color="red" style={{ margin: 0 }}>{val}</Tag>
        </Tooltip>
      ) : (
        <span>{val}</span>
      ),
  },
  {
    title: L('Sale (£)'),
    dataIndex: 'sellPrice',
    key: 'sellPrice',
    width: 75,
    render: (val: number) => val?.toFixed(2),
    exportRender: (val: number) => val?.toFixed(2),
  },
  {
    title: L('Regular item'),
    dataIndex: 'isInShop',
    key: 'isInShop',
    width: 90,
    render: (val: boolean, record: Inventory) =>
      val ? (
        <Tooltip title={record.location || L('No location set')}>
          <Tag color="green" style={{ margin: 0 }}>
            ✓ {record.location ? record.location : L('Regular item')}
          </Tag>
        </Tooltip>
      ) : (
        <Tag style={{ margin: 0, color: '#8c8c8c' }}>-</Tag>
      ),
    exportRender: (val: boolean, record: Inventory) =>
      val ? (record.location ? `In shop (${record.location})` : 'Regular item') : 'No',
  },
  {
    title: L('Action'),
    key: 'action',
    width: 90,
    render: (record: Inventory) => (
      <Space size={4}>
        <Tooltip title={L('Duplicate')}>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(record);
            }}
          />
        </Tooltip>
        <Tooltip title={record.quantity > 0 ? L('Return to supplier') : L('No stock to return')}>
          <Button
            size="small"
            icon={<RollbackOutlined />}
            disabled={record.quantity <= 0}
            onClick={(e) => {
              e.stopPropagation();
              onReturnToSupplier(record);
            }}
          />
        </Tooltip>
        <Tooltip title={L('Add to purchase orders')}>
          <Button
            size="small"
            icon={<ShoppingCartOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onAddToPurchaseOrder(record);
            }}
          />
        </Tooltip>
      </Space>
    ),
  },
];

const formatDateUk = (val: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '');

export const ExportColumns = [
  { title: L('Purchase Date'), dataIndex: 'purchaseDate', exportRender: formatDateUk },
  { title: L('Date added'), dataIndex: 'creationTime', exportRender: formatDateUk },
  { title: L('Supplier'), dataIndex: 'supplierName' },
  { title: L('Supplier Inv #'), dataIndex: 'supplierInvoiceNumber' },
  { title: L('Supplier Part #'), dataIndex: 'partNumber' },
  { title: L('Garage Ref #'), dataIndex: 'reference' },
  { title: L('Part Name'), dataIndex: 'name' },
  { title: L('Description'), dataIndex: 'description' },
  { title: L('Category'), dataIndex: 'categoryName' },
  { title: L('Qty'), dataIndex: 'quantity' },
  { title: L('Regular item'), dataIndex: 'isInShop', exportRender: (val: boolean) => (val ? 'Yes' : 'No') },
  { title: L('Min Stock'), dataIndex: 'minStockLevel' },
  { title: L('Puchase £'), dataIndex: 'costPrice', exportRender: (val: number) => (val ?? 0).toFixed(2) },
  { title: L('Retail £'), dataIndex: 'sellPrice', exportRender: (val: number) => (val ?? 0).toFixed(2) },
  { title: L('Margin %'), dataIndex: 'margin', exportRender: (val: number) => (val != null ? `${val.toFixed(1)}%` : '') },
  { title: L('Part Location'), dataIndex: 'location' },
  { title: L('Umoto ID'), dataIndex: 'id' },
];
